import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import { faClass } from '@/constants/icons';

import AppButton from '../buttons/AppButton';

import s from './AppMenuButton.module.scss';

/**
 * AppMenuButton — bottone che apre un menù di azioni.
 *
 * Il trigger è un `AppButton` (stesse `mode`/`variant`/`size`/`icon`), quindi
 * un menù non stona accanto agli altri bottoni di una toolbar.
 *
 * `items`: `[{ key, label, icon, onSelect, disabled }]`. `icon` accetta sia una
 * classe completa (`ICONS.add`) sia un glifo nudo (`'ambulance'`, come li tiene
 * `SERVICE_MODE_META`): ci pensa `faClass`.
 *
 * A11y: `aria-haspopup`/`aria-expanded` sul trigger, `role="menu"`/`menuitem`
 * sulle voci, chiusura con Escape e con un click fuori, focus che torna al
 * trigger dopo la chiusura da tastiera.
 */
export default function AppMenuButton({
  items = [],
  label,
  icon,
  mode = 'nav',
  variant = 'primary',
  size = 'md',
  title,
  disabled = false,
  align = 'start',
  className,
}) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const menuId = useId();

  const close = useCallback((refocus = false) => {
    setOpen(false);
    if (refocus) triggerRef.current?.querySelector('button')?.focus();
  }, []);

  // Listener globali agganciati SOLO da aperto: un menù chiuso non deve
  // costare nulla, e in una toolbar ce ne può essere più d'uno.
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close(true);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  // Ribalta verso l'alto quando sotto non c'è spazio per il pannello.
  useEffect(() => {
    if (!open) return;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (rect) setOpenUp(window.innerHeight - rect.bottom < 40 + items.length * 44);
  }, [open, items.length]);

  const handleSelect = (item) => {
    setOpen(false);
    item.onSelect?.();
  };

  const menuClasses = [s.menu, openUp ? s.menuUp : null, align === 'end' ? s.menuEnd : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={[s.wrap, className].filter(Boolean).join(' ')} ref={wrapRef}>
      <div ref={triggerRef}>
        <AppButton
          mode={mode}
          variant={variant}
          size={size}
          icon={icon}
          title={title}
          disabled={disabled}
          onClick={() => setOpen((prev) => !prev)}
          ariaExpanded={open}
          ariaControls={menuId}
        >
          {label}
        </AppButton>
      </div>

      {open && (
        // <div> e non <ul>: il menù ARIA possiede direttamente i `menuitem`,
        // senza <li> intermedi da neutralizzare con role="none".
        <div className={menuClasses} id={menuId} role="menu">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className={s.item}
              disabled={item.disabled}
              onClick={() => handleSelect(item)}
            >
              {item.icon && <i className={faClass(item.icon)} aria-hidden="true" />}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
